import React from 'react';
import { ArrowDownWideNarrow, Plus, Trash2, ArrowUp, ArrowDown } from 'lucide-react';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';

interface SortOrder {
    column: string;
    direction: 'ASC' | 'DESC';
}

export const SortNodeConfig = ({ data, onChange, inputColumns = [] }: any) => {
    const columns: SortOrder[] = data.columns || [];
    const limit = data.limit;

    const addSortColumn = () => {
        const newColumns = [...columns, { column: '', direction: 'ASC' }];
        onChange('columns', newColumns);
    };

    const removeSortColumn = (index: number) => {
        const newColumns = columns.filter((_, i) => i !== index);
        onChange('columns', newColumns);
    };

    const updateSortColumn = (index: number, field: keyof SortOrder, value: any) => {
        const newColumns = [...columns];
        newColumns[index] = { ...newColumns[index], [field]: value };
        onChange('columns', newColumns);
    };

    const updateLimit = (val: string) => {
        const num = val ? parseInt(val) : null;
        onChange('limit', num);
    };

    return (
        <div className="space-y-6">
            <h3 className="font-semibold text-gray-900 text-xs uppercase tracking-wider mb-2 flex items-center gap-2">
                <ArrowDownWideNarrow className="h-3 w-3 text-blue-500" />
                Sort Configuration
            </h3>

            {/* Sort Columns List */}
            <div className="space-y-3">
                <div className="flex justify-between items-center">
                    <Label className="text-xs text-gray-500 font-medium uppercase">Sort By</Label>
                    <Button
                        variant="ghost"
                        size="sm"
                        className="h-6 text-[10px] text-blue-600 hover:text-blue-700 hover:bg-blue-50 px-2"
                        onClick={addSortColumn}
                    >
                        <Plus className="h-3 w-3 mr-1" />
                        Add Column
                    </Button>
                </div>

                {columns.length === 0 ? (
                    <div className="text-center p-4 border border-dashed border-gray-200 rounded-lg bg-gray-50 text-xs text-gray-400">
                        No sort columns defined.
                    </div>
                ) : (
                    <div className="space-y-2">
                        {columns.map((col, idx) => (
                            <div key={idx} className="flex gap-2 items-start animate-in fade-in slide-in-from-left-2 duration-200">
                                {/* Column Selector */}
                                <div className="flex-1">
                                    <Select
                                        value={col.column}
                                        onValueChange={(val) => updateSortColumn(idx, 'column', val)}
                                    >
                                        <SelectTrigger className="h-8 text-xs bg-white">
                                            <SelectValue placeholder="Select Column" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {inputColumns.map((c: any) => (
                                                <SelectItem key={c.name} value={c.name} className="text-xs">
                                                    {c.name}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>

                                {/* Direction Toggle */}
                                <div className="w-24">
                                    <Select
                                        value={col.direction}
                                        onValueChange={(val) => updateSortColumn(idx, 'direction', val)}
                                    >
                                        <SelectTrigger className="h-8 text-xs bg-white">
                                            <div className="flex items-center gap-2">
                                                {col.direction === 'ASC' ? <ArrowUp className="h-3 w-3 text-gray-400" /> : <ArrowDown className="h-3 w-3 text-gray-400" />}
                                                <SelectValue />
                                            </div>
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="ASC" className="text-xs">Ascending</SelectItem>
                                            <SelectItem value="DESC" className="text-xs">Descending</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>

                                {/* Remove Button */}
                                <Button
                                    variant="ghost"
                                    size="icon"
                                    className="h-8 w-8 text-gray-400 hover:text-red-500 hover:bg-red-50"
                                    onClick={() => removeSortColumn(idx)}
                                >
                                    <Trash2 className="h-3 w-3" />
                                </Button>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* Limit Option */}
            <div className="pt-4 border-t border-gray-100">
                <div className="space-y-1.5">
                    <Label className="text-xs text-gray-500 font-medium">Limit Rows (Optional)</Label>
                    <Input
                        type="number"
                        placeholder="e.g. 100"
                        className="h-8 text-xs bg-white"
                        value={limit || ''}
                        onChange={(e) => updateLimit(e.target.value)}
                    />
                    <p className="text-[10px] text-gray-400">
                        Leave empty to return all sorted rows.
                    </p>
                </div>
            </div>
        </div>
    );
};
